import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { tglPanjang, HARI } from "../lib/constants";
import { PageTitle, Card, Field, Input, Textarea, FileUpload, Avatar, InfoGrid, Loading } from "../components/Shared";
import { Eye, EyeOff, KeyRound } from "lucide-react";

function AkunCard({ role }) {
  const label = role === "admin" ? "Admin" : "Koordinator";
  const [akun, setAkun] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/akun/${role}`).then((r) => setAkun(r.data)).catch(() => setAkun({ username: "", password: "" })); }, [role]);
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put(`/akun/${role}`, akun); toast.success(`Akun ${label.toLowerCase()} diperbarui`); } catch (ex) { toast.error(errMsg(ex)); } finally { setSaving(false); }
  };
  if (!akun) return null;
  return (
    <Card className="p-6 sm:p-8 mt-6" data-testid={`akun-${role}-card`}>
      <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2"><KeyRound size={18} className="text-emerald-600" /> Akun Login {label}</h3>
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={`akun-${role}-form`}>
        <Field label={`Username ${label}`} required><Input data-testid={`akun-${role}-username`} value={akun.username} onChange={(e) => setAkun({ ...akun, username: e.target.value })} required /></Field>
        <Field label={`Password ${label}`} required>
          <div className="relative">
            <Input type={show ? "text" : "password"} data-testid={`akun-${role}-password`} value={akun.password} onChange={(e) => setAkun({ ...akun, password: e.target.value })} required className="input pr-10" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600" data-testid={`akun-${role}-toggle`}>{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
          </div>
        </Field>
        <div className="sm:col-span-2 flex justify-end"><button className="btn-primary" disabled={saving} data-testid={`akun-${role}-save-btn`}>{saving ? "Menyimpan..." : "Simpan Akun"}</button></div>
      </form>
      {role === "admin" && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-4">Perhatian: mengubah username/password admin akan mengganti kredensial login Anda. Catat baik-baik sebelum menyimpan.</p>}
    </Card>
  );
}

export function ProfilAdminKoord({ role }) {
  const { user, refresh } = useAuth();
  const canEdit = user.role === "admin";
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(null); api.get(`/profil/${role}`).then((r) => setF(r.data)); }, [role]);
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put(`/profil/${role}`, f); toast.success("Profil disimpan"); if (role === user.role) refresh(); } catch (ex) { toast.error(errMsg(ex)); } finally { setSaving(false); }
  };
  if (!f) return <Loading />;
  const title = role === "admin" ? "Profil Admin" : "Profil Koordinator";
  return (
    <div>
      <PageTitle title={title} />
      <Card className="p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="flex flex-col items-center gap-3">
            <Avatar src={f.foto} name={f.nama} size={24} />
            <div className="font-bold text-slate-900 text-center">{f.nama}</div>
            {canEdit && <FileUpload value={f.foto} onChange={(p) => setF({ ...f, foto: p })} accept="image/*" label="Upload foto" testId="profil-foto-upload" />}
          </div>
          <form onSubmit={save} className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="profil-form">
            <Field label="Nama Lengkap" className="sm:col-span-2"><Input data-testid="profil-nama" value={f.nama || ""} onChange={(e) => setF({ ...f, nama: e.target.value })} disabled={!canEdit} required /></Field>
            <Field label="Tempat Lahir"><Input data-testid="profil-tempat-lahir" value={f.tempat_lahir || ""} onChange={(e) => setF({ ...f, tempat_lahir: e.target.value })} disabled={!canEdit} /></Field>
            <Field label="Tanggal Lahir"><Input type="date" data-testid="profil-tanggal-lahir" value={f.tanggal_lahir || ""} onChange={(e) => setF({ ...f, tanggal_lahir: e.target.value })} disabled={!canEdit} /></Field>
            <Field label="No. Telp"><Input data-testid="profil-telp" value={f.telp || ""} onChange={(e) => setF({ ...f, telp: e.target.value })} disabled={!canEdit} /></Field>
            <Field label="Alamat" className="sm:col-span-2"><Textarea data-testid="profil-alamat" value={f.alamat || ""} onChange={(e) => setF({ ...f, alamat: e.target.value })} disabled={!canEdit} /></Field>
            {canEdit && <div className="sm:col-span-2 flex justify-end"><button className="btn-primary" disabled={saving} data-testid="profil-save-btn">{saving ? "Menyimpan..." : "Simpan Profil"}</button></div>}
          </form>
        </div>
      </Card>
      {canEdit && <AkunCard role={role} />}
    </div>
  );
}

export function SiswaDetailView({ s }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-5">
        <Avatar src={s.foto} name={s.nama} size={20} />
        <div>
          <div className="text-xl font-extrabold text-slate-900" data-testid="siswa-detail-nama">{s.nama}</div>
          <div className="text-sm text-slate-500">{s.ketunaan} · {s.sekolah}</div>
        </div>
      </div>
      <InfoGrid items={[["Tempat Lahir", s.tempat_lahir], ["Tanggal Lahir", tglPanjang(s.tanggal_lahir)], ["Jenis Kelamin", s.jenis_kelamin], ["Status", s.status], ["Ketunaan", s.ketunaan], ["Nama Sekolah", s.sekolah], ["Alamat", s.alamat], ["Guru Pendamping", s.guru_nama || s.guru?.nama]]} />
      <div>
        <h4 className="font-bold text-slate-800 mb-3">Jadwal Belajar</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {HARI.map((h) => <div key={h} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center"><div className="text-xs font-bold text-emerald-700">{h}</div><div className="text-xs text-slate-600 mt-1 font-mono">{s.jadwal?.[h]?.masuk || "-"} – {s.jadwal?.[h]?.pulang || "-"}</div></div>)}
        </div>
      </div>
      <div>
        <h4 className="font-bold text-slate-800 mb-3">Data Orang Tua</h4>
        <InfoGrid items={[["Nama Ayah", s.ayah], ["Nama Ibu", s.ibu], ["Pekerjaan Ayah", s.pekerjaan_ayah], ["Pekerjaan Ibu", s.pekerjaan_ibu], ["No. Telp Ayah", s.telp_ayah], ["No. Telp Ibu", s.telp_ibu], ["Email Orang Tua", s.email_ortu]]} />
      </div>
    </div>
  );
}

export function GuruDetailView({ g }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-5">
        <Avatar src={g.foto} name={g.nama} size={20} />
        <div>
          <div className="text-xl font-extrabold text-slate-900" data-testid="guru-detail-nama">{g.nama}</div>
          <div className="text-sm text-slate-500">{g.pendidikan}</div>
        </div>
      </div>
      <InfoGrid items={[["Tempat Lahir", g.tempat_lahir], ["Tanggal Lahir", tglPanjang(g.tanggal_lahir)], ["Jenis Kelamin", g.jenis_kelamin], ["Pendidikan", g.pendidikan], ["Status", g.status], ["Email", g.email], ["No. Telp", g.telp], ["Alamat", g.alamat], ["Siswa Pendampingan", g.siswa?.nama || g.siswa_nama]]} />
      <div>
        <h4 className="font-bold text-slate-800 mb-3">Data Orang Tua</h4>
        <InfoGrid items={[["Nama Ayah", g.ayah], ["Nama Ibu", g.ibu], ["Pekerjaan Ayah", g.pekerjaan_ayah], ["Pekerjaan Ibu", g.pekerjaan_ibu], ["No. Telp Ayah", g.telp_ayah], ["No. Telp Ibu", g.telp_ibu]]} />
      </div>
    </div>
  );
}

export function ProfilSiswaPage() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/siswa").then((r) => setS(r.data.items[0] || false)); }, []);
  if (s === null) return <Loading />;
  return <div><PageTitle title="Profil Siswa Pendampingan" /><Card className="p-6 sm:p-8">{s ? <SiswaDetailView s={s} /> : <p className="text-slate-500">Belum ada siswa pendampingan yang ditugaskan.</p>}</Card></div>;
}

export function ProfilGuruPage() {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/guru").then(async (r) => { const it = r.data.items[0]; if (!it) return setG(false); const d = await api.get(`/guru/${it.id}`); setG(d.data); }); }, []);
  if (g === null) return <Loading />;
  return <div><PageTitle title="Profil Guru Pendamping" /><Card className="p-6 sm:p-8">{g ? <GuruDetailView g={g} /> : <p className="text-slate-500">Guru pendamping belum ditentukan.</p>}</Card></div>;
}
